import React from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Box from '../../elements/Box';
import OriginDestinationFilter from './OriginDestinationFilterMobile';
import NavigationHeader from '../../elements/NavigationHeader';
import {
  enterFullMode,
  exitFullMode,
  notifyFullMode,
  notifyExitFullMode
} from '../../containers/_commons/navigation';
import { DialogOverlay, DialogContent } from '@reach/dialog';
import Button from '../../elements/Button';
import Icon from '../../elements/Icon';

const InlineFilters = ({
  clearButtonDisplay,
  sortingDisplay,
  showLabels,
  locations,
  onClear,
  availableFilters,
  containerSettings,
  clearAllSettings: { showIcon, icon: clearAllIcon, ...clearAllSettings },
  clearAllColumnSettings,
  containerOnFullScreenSettings,
  originDestinationSettings,
  children,
  headerLocation,
  header,
  headerSettings,
  dialogSettings,
  enableExperimentalNavigation,
  dispatch,
  navigation,
  isFullMode,
  ...props
}) => {
  const { translate } = React.useContext(Context);
  const ref = React.useRef();
  const isDirty = !!(locations.origin.value || locations.destination.value);
  const useClassicNavigation = !enableExperimentalNavigation;
  const originLocationSelectorRef = React.useRef(null);

  const goBack = () => {
    setTimeout(() => {
      originLocationSelectorRef?.current?.onCloseMenu();
      dispatch({ type: 'NAVIGATION_CHANGE', payload: 'all' });
      notifyExitFullMode();
      if (useClassicNavigation) {
        exitFullMode(ref);
        dispatch({ type: 'FULL_MODE', payload: false });
      }
    }, 150);
  };

  const getNavigationProps = (
    key,
    { onClear, onFocus, onChange, onClick, avoidCloseFilter = false, ...rest }
  ) => {
    return {
      onClear: (event) => {
        let notifyFullMode = event?.notifyFullMode ?? true;
        if (onClear) {
          onClear(event);
        }
        if (notifyFullMode) {
          dispatch({ type: 'NAVIGATION_CHANGE', payload: key });
          if (!isFullMode) {
            typeof notifyFullMode === 'function' && notifyFullMode();
          }
          if (useClassicNavigation) {
            enterFullMode(ref);
            dispatch({ type: 'FULL_MODE', payload: true });
          }
        }
      },
      onFocus: (event) => {
        if (onFocus) {
          onFocus(event);
        }
        dispatch({ type: 'NAVIGATION_CHANGE', payload: key });
        if (!isFullMode) {
          notifyFullMode();
        }
        if (useClassicNavigation) {
          enterFullMode(ref);
          dispatch({ type: 'FULL_MODE', payload: true });
        }
      },
      onClick: (event) => {
        if (onClick) {
          onClick(event);
        }
        dispatch({ type: 'NAVIGATION_CHANGE', payload: key });
        if (!isFullMode) {
          notifyFullMode();
        }
        if (useClassicNavigation) {
          enterFullMode(ref);
          dispatch({ type: 'FULL_MODE', payload: true });
        }
      },
      onChange: (event) => {
        if (onChange) {
          onChange(event);
        }
        if (!Array.isArray(event)) {
          if (!avoidCloseFilter) {
            goBack();
          }
        }
      },
      ...rest
    };
  };

  const elementsWrappers = {
    OriginDestination: (e) => {
      return e;
    },
    ClearButton: (e) => (
      <Col xs="12" key="clearAllBtn" textAlign="right" {...clearAllColumnSettings}>
        {e}
      </Col>
    )
  };

  const handleClear = () => {
    onClear();
    if (elementRefDatePicker.current?.reset !== undefined) {
      elementRefDatePicker.current.reset(false);
    }
  };

  const elementRefDatePicker = React.useRef(null);

  const elements = {
    OriginDestination: (
      <OriginDestinationFilter
        key="OriginDestination"
        navigation={navigation}
        showLabels={showLabels}
        {...originDestinationSettings}
        {...locations}
        origin={getNavigationProps('Origin', locations.origin)}
        destination={getNavigationProps('Destination', locations.destination)}
        isFullMode={isFullMode}
        allowOverlayMultiple={true}
        originLocationSelectorRef={originLocationSelectorRef}
      />
    ),
    ClearButton: (
      <Button
        paddingWidth="0"
        textTransform="uppercase"
        fontSize="3"
        onClick={handleClear}
        disabled={!isDirty}
        cursor={isDirty ? 'pointer' : 'default'}
        dataTestId="clearAllBtn"
        display="inline-flex"
        alignItems="center"
        {...clearAllSettings}
      >
        {translate('cmp-filter-clear', 'Clear all')}
        {showIcon && <Icon icon="close" paddingLeft="1" size={18} {...clearAllIcon} />}
      </Button>
    )
  };

  let fullFiltersList = availableFilters;
  if (clearButtonDisplay === 'end') {
    fullFiltersList = [...availableFilters, 'ClearButton'];
  } else if (clearButtonDisplay === 'start') {
    fullFiltersList = ['ClearButton', ...availableFilters];
  }

  const filtersToDisplay = fullFiltersList.filter((e) => {
    let iterator = e;
    if (typeof iterator === 'object') {
      iterator = e.type;
    }
    return elements[iterator] && (sortingDisplay === 'inline' || iterator !== 'Sorting');
  });

  const [classNameDialog, styleDialog] = useStyles(dialogSettings);
  const myStyle = Object.assign(
    {
      width: '100vw',
      height: '100vh',
      padding: 0,
      margin: 0,
      overflow: 'hidden',
      background: 'white'
    },
    styleDialog
  );

  const overlayStyle = {
    zIndex: 999999,
    position: 'fixed',
    top: '0px',
    right: '0px',
    bottom: '0px',
    left: '0px',
    overflow: 'auto'
  };

  if (navigation !== 'all' && enableExperimentalNavigation) {
    return (
      <DialogOverlay style={overlayStyle}>
        <DialogContent className={classNameDialog} style={myStyle}>
          {/* FIXME: Conditional styles for DateRangeSelector, the new component should not need this. */}
          <Box position={navigation === 'all' ? 'relative' : undefined} height="100" {...props}>
            <NavigationHeader
              headerLocation={headerLocation}
              title={translate(`cmp-header-mobile-${navigation.toLowerCase()}`)}
              onClickIcon={goBack}
              styleIcon={{
                transform: 'rotate(90deg)',
                display: 'inline-block'
              }}
              {...headerSettings}
              focusBehavior
            />
            <Row gutters {...containerSettings} {...containerOnFullScreenSettings}>
              {navigation === 'Origin' || navigation === 'Destination'
                ? elements.OriginDestination
                : elements[navigation]}
            </Row>
          </Box>
        </DialogContent>
      </DialogOverlay>
    );
  }

  const wrapperRender = elementsWrappers[navigation];

  return (
    <Box position={navigation === 'all' ? 'relative' : undefined} focusReferences={ref} {...props}>
      {navigation === 'all' ? (
        <>
          {header}
          <Row gutters marginBottom="2" {...containerSettings}>
            {filtersToDisplay.map((ele) => {
              let e = ele;
              if (typeof e === 'object') {
                e = e.type;
              }
              const wrapper = elementsWrappers[e];
              const element = wrapper(elements[e]);
              return element;
            })}
          </Row>
          {children}
        </>
      ) : (
        <>
          <NavigationHeader
            headerLocation={headerLocation}
            title={translate(`cmp-header-mobile-${navigation.toLowerCase()}`)}
            onClickIcon={goBack}
            styleIcon={{
              transform: 'rotate(90deg)',
              display: 'inline-block'
            }}
            {...headerSettings}
            focusBehavior
          />
          <Row
            gutters
            {...containerSettings}
            paddingWidth="15px !important"
            {...containerOnFullScreenSettings}
          >
            {navigation === 'Origin' || navigation === 'Destination'
              ? elements.OriginDestination
              : wrapperRender(elements[navigation])}
          </Row>
        </>
      )}
    </Box>
  );
};

InlineFilters.propTypes = {
  clearButtonDisplay: PropTypes.oneOf(['start', 'end', 'hidden']),
  sortingDisplay: PropTypes.oneOf(['inline', 'below-filters']),
  showLabels: PropTypes.bool,
  availableFilters: PropTypes.arrayOf(PropTypes.oneOf(['OriginDestination'])),
  containerSettings: PropTypes.objectOf(PropTypes.any),
  containerOnFullScreenSettings: PropTypes.objectOf(PropTypes.any),
  clearAllSettings: PropTypes.objectOf(PropTypes.any),
  clearAllColumnSettings: PropTypes.objectOf(PropTypes.any),
  originDestinationSettings: PropTypes.objectOf(PropTypes.any),
  headerSettings: PropTypes.objectOf(PropTypes.any),
  dialogSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters state and actions
   */
  locations: PropTypes.shape({
    origin: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    destination: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    swapLocations: PropTypes.func
  }),
  onClear: PropTypes.func,
  children: PropTypes.node,
  header: PropTypes.node,
  headerLocation: PropTypes.string,
  enableExperimentalNavigation: PropTypes.bool
};

InlineFilters.defaultProps = {
  clearButtonDisplay: 'hidden',
  sortingDisplay: 'inline',
  showLabels: false,
  availableFilters: ['OriginDestination'],
  containerSettings: {
    padding: '1'
  },
  containerOnFullScreenSettings: {},
  originDestinationSettings: {},
  clearAllSettings: {
    color: 'link',
    bgColor: 'transparent',
    textColor: 'light-18',
    showIcon: true,
    borderNone: true,
    icon: {
      iconColor: 'light-18'
    }
  },
  clearAllColumnSettings: {},
  locations: { origin: {}, destination: {} },
  children: null,
  headerLocation: undefined,
  header: null,
  dialogSettings: {},
  onClear: null,
  enableExperimentalNavigation: false
};

export default InlineFilters;
